/**
 * Pure layout math for the custom DOM grid table (F-10 rewritten 2026-07-10,
 * F-12b, F-14, F-26 — docs/04 §5 "custom DOM table, CSS-transform zoom").
 *
 * The table renders at full size in BOARD units inside the CSS-transformed
 * board layer, so everything here is board-unit math with no DOM access —
 * unit-testable, and shared between rendering, hover-handle hit-testing, and
 * F-30 drop targeting.
 *
 * F-26 column sizing (verified against the CSS options): a naive
 * `fit-content(480px)` track would size EVERY column to its max-content up to
 * 480 px, so narrowing a column manually (F-14) could never make wrappable
 * text wrap tighter than 480 — violating "manual resize sets a minimum" +
 * "text wraps within the cell". Instead tracks are explicit px widths:
 *
 *   width(col) = max(manualWidth, min(480, widestUnwrappableRun + padding))
 *
 * with `overflow-wrap: anywhere` in cells so runs longer than the 480 cap
 * hard-wrap. Wrappable text wraps at the manual width; only unwrappable runs
 * (long words, URLs, numbers) widen the column — exactly F-26. Auto-widened
 * sizes are DERIVED client state; only manual resizes write to the doc.
 */

/* ------------------------------------------------------------------ */
/* Base metrics (board units, zoom 1)                                  */
/* ------------------------------------------------------------------ */

export const CELL_H_PAD = 8;
export const CELL_V_PAD = 4;
/** Extra slack over the measured run so canvas-vs-DOM rounding never wraps it. */
export const RUN_WIDTH_SLOP = 3;
/** F-26: auto-widen never exceeds this many board px. */
export const AUTO_WIDEN_CAP_BOARD_PX = 480;
/** F-26/F-30: a cell image thumbnail is at most this many board px tall. */
export const IMAGE_THUMB_MAX_BOARD_PX = 160;

/** Manual column width bounds in board px (F-14). */
export const MIN_MANUAL_COL_WIDTH = 40;
export const MAX_MANUAL_COL_WIDTH = 800;
/** Manual row height bounds in board px (F-14). */
export const MIN_MANUAL_ROW_HEIGHT = 20;
export const MAX_MANUAL_ROW_HEIGHT = 2000;

export const TABLE_FONT_PX = 13;
export const TABLE_LINE_HEIGHT = 1.4;
/** Must match the .gt-cell CSS font stack — the run measurer uses it. */
export const TABLE_FONT_FAMILY =
  "Inter, Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
export const TABLE_FONT = `${TABLE_FONT_PX}px ${TABLE_FONT_FAMILY}`;

/** 1px table/cell borders participate in edge positions. */
export const TABLE_BORDER = 1;

export function clampManualColWidth(width: number): number {
  return Math.min(MAX_MANUAL_COL_WIDTH, Math.max(MIN_MANUAL_COL_WIDTH, Math.round(width)));
}

export function clampManualRowHeight(height: number): number {
  return Math.min(MAX_MANUAL_ROW_HEIGHT, Math.max(MIN_MANUAL_ROW_HEIGHT, Math.round(height)));
}

/* ------------------------------------------------------------------ */
/* F-26 run-width capping                                              */
/* ------------------------------------------------------------------ */

/**
 * Whitespace where CSS actually allows a line break under `pre-wrap`.
 * Deliberately NOT `\s`: JS `\s` matches the no-break spaces U+00A0/U+202F,
 * the figure space U+2007, and the zero-width no-break space U+FEFF — none of
 * which CSS breaks at, so splitting on them under-measures NBSP-joined runs
 * (they'd hard-wrap mid-word via `overflow-wrap: anywhere` instead of
 * widening the column, violating F-26). U+200B (zero-width space) IS a break
 * opportunity and is included.
 */
const BREAKABLE_WS =
  /[\t\n\v\f\r \u1680\u2000-\u2006\u2008-\u200b\u2028\u2029\u205f\u3000]/;

/**
 * True where CSS actually allows a line break on whitespace (BREAKABLE_WS).
 *
 * Exported because core/measure.ts's row-height emulator MUST classify
 * soft-wrap opportunities exactly as this F-26 pass does: if the two disagree
 * about what "unwrappable" means, column widening and row-height prediction
 * disagree about the same cell, and the predicted edges stop matching what the
 * browser renders. One definition, two consumers.
 */
export function isBreakableWhitespace(ch: string): boolean {
  return BREAKABLE_WS.test(ch);
}

/**
 * CSS can break BETWEEN CJK characters (ideographs, kana, hangul, fullwidth
 * forms) with default `line-break`/`word-break`, so a CJK sequence is never
 * an unwrappable run — each character wraps on its own. Without this, a long
 * whitespace-free CJK sentence would be measured as one giant run and widen
 * the column to the 480 px cap for no reason.
 *
 * Exported for core/measure.ts — see `isBreakableWhitespace`.
 */
export function isCjkBreakable(cp: number): boolean {
  return (
    (cp >= 0x2e80 && cp <= 0x303f) || // CJK radicals, Kangxi, CJK symbols
    (cp >= 0x3040 && cp <= 0x30ff) || // hiragana, katakana
    (cp >= 0x3130 && cp <= 0x318f) || // hangul compatibility jamo
    (cp >= 0x31c0 && cp <= 0x9fff) || // strokes, katakana ext, CJK unified (+ext A)
    (cp >= 0xac00 && cp <= 0xd7af) || // hangul syllables
    (cp >= 0xf900 && cp <= 0xfaff) || // CJK compatibility ideographs
    (cp >= 0xfe30 && cp <= 0xfe4f) || // CJK compatibility forms
    (cp >= 0xff00 && cp <= 0xffef) || // full/half-width forms
    (cp >= 0x20000 && cp <= 0x3ffff) // CJK extensions B+
  );
}

/**
 * Unwrappable runs of a text — maximal segments CSS cannot break inside
 * (words, URLs, numbers, NBSP-joined phrases). We enable
 * `overflow-wrap: anywhere` only as the last resort at the 480 px cap, so
 * anything between genuine break opportunities is an unwrappable run.
 */
export function splitUnwrappableRuns(text: string): readonly string[] {
  const runs: string[] = [];
  let current = "";
  const flush = () => {
    if (current.length > 0) {
      runs.push(current);
      current = "";
    }
  };
  for (const ch of text) {
    if (BREAKABLE_WS.test(ch)) {
      flush();
      continue;
    }
    if (isCjkBreakable(ch.codePointAt(0) ?? 0)) {
      // Breakable on both sides: ends the current run, is its own run.
      flush();
      runs.push(ch);
      continue;
    }
    current += ch;
  }
  flush();
  return runs;
}

/** Widest unwrappable run in px under `measure` (0 for empty text). */
export function maxRunWidth(text: string, measure: (run: string) => number): number {
  let max = 0;
  for (const run of splitUnwrappableRuns(text)) {
    const w = measure(run);
    if (w > max) max = w;
  }
  return max;
}

/**
 * Widest LINE of a text in px under `measure` (0 for empty text). F-14
 * double-click autofit fits whole content lines (Sheets behavior), unlike the
 * F-26 pass which only measures unwrappable runs.
 */
export function maxLineWidth(text: string, measure: (line: string) => number): number {
  let max = 0;
  for (const line of text.split("\n")) {
    if (line.length === 0) continue;
    const w = measure(line);
    if (w > max) max = w;
  }
  return max;
}

/**
 * F-14 double-click autofit: the manual width that fits the column's widest
 * content line (plus cell padding), clamped to the manual F-14 bounds.
 * Empty columns fall back to `defaultWidth`.
 */
export function autofitColumnWidth(maxContentPx: number, defaultWidth: number): number {
  if (maxContentPx <= 0) return defaultWidth;
  return clampManualColWidth(Math.ceil(maxContentPx) + 2 * CELL_H_PAD + RUN_WIDTH_SLOP);
}

/**
 * F-26 effective column width: the manual width (F-14) is a FLOOR; the widest
 * unwrappable run may widen the column up to the 480 board-px cap (then the
 * run hard-wraps via `overflow-wrap: anywhere`). Whole px.
 */
export function autoColumnWidth(
  maxRunPx: number,
  manualWidth: number,
  capPx: number = AUTO_WIDEN_CAP_BOARD_PX,
): number {
  const wanted = maxRunPx <= 0 ? 0 : Math.ceil(maxRunPx) + 2 * CELL_H_PAD + RUN_WIDTH_SLOP;
  return Math.max(manualWidth, Math.min(capPx, wanted));
}

/**
 * Displayed size of a cell image thumbnail (F-30/F-26): fit the natural size
 * into max 160 board px tall (and the column cap wide), keep aspect, never
 * upscale.
 */
export function imageThumbSize(
  natW: number,
  natH: number,
  maxH: number = IMAGE_THUMB_MAX_BOARD_PX,
  maxW: number = AUTO_WIDEN_CAP_BOARD_PX - 2 * CELL_H_PAD,
): { w: number; h: number } {
  if (natW <= 0 || natH <= 0) return { w: 0, h: 0 };
  const scale = Math.min(1, maxH / natH, maxW / natW);
  return {
    w: Math.max(1, Math.round(natW * scale)),
    h: Math.max(1, Math.round(natH * scale)),
  };
}

/* ------------------------------------------------------------------ */
/* Edges & hit-testing (board units, relative to the table wrapper)    */
/* ------------------------------------------------------------------ */

/**
 * Column edge positions from track widths: edges[i] = left edge of column i,
 * edges[n] = the table's right content edge. Offset by the table border so
 * they line up with cell offsetLeft/offsetTop.
 */
export function edgesFromSizes(sizes: readonly number[]): number[] {
  const edges: number[] = [TABLE_BORDER];
  let acc = TABLE_BORDER;
  for (const s of sizes) {
    acc += s;
    edges.push(acc);
  }
  return edges;
}

/**
 * `ids[i] → i` — the O(1) counterpart of `ids.indexOf(id)`.
 *
 * One definition because it is published on `GridLayoutSnapshot` and consumed
 * by every anchor resolution (comments, contained elements, selection rects).
 * A scan does not scale to F-10's 10k-row target: it runs per pin and per
 * element on every render, which includes every peer awareness tick (~30/s) and
 * every zoom frame.
 */
export function indexById(ids: readonly string[]): ReadonlyMap<string, number> {
  const map = new Map<string, number>();
  ids.forEach((id, i) => map.set(id, i));
  return map;
}

/** Index of the interval `[edges[i], edges[i+1])` containing pos, or -1. */
export function indexAtPosition(edges: readonly number[], pos: number): number {
  for (let i = 0; i + 1 < edges.length; i++) {
    const lo = edges[i];
    const hi = edges[i + 1];
    if (lo !== undefined && hi !== undefined && pos >= lo && pos < hi) return i;
  }
  return -1;
}

/**
 * Boundary index (0..n) whose edge lies within `tolerance` of pos, or null.
 * Boundary b sits BEFORE item b; boundary n is the append-at-end edge.
 */
export function nearestBoundary(
  edges: readonly number[],
  pos: number,
  tolerance: number,
): number | null {
  let best: number | null = null;
  let bestDist = tolerance;
  for (let b = 0; b < edges.length; b++) {
    const edge = edges[b];
    if (edge === undefined) continue;
    const d = Math.abs(pos - edge);
    if (d <= bestDist) {
      bestDist = d;
      best = b;
    }
  }
  return best;
}

/**
 * F-12b pill drag-reorder: where a drag at `pos` would insert.
 * Returns the insertion BOUNDARY (0..n, for the drop indicator line) and the
 * `moveRow`/`moveColumn` target index (position among the OTHER items —
 * docs/07 C1: the move is a single fractional-ord write at that index).
 */
export function reorderTarget(
  edges: readonly number[],
  fromIndex: number,
  pos: number,
): { boundary: number; moveIndex: number } {
  const count = edges.length - 1;
  let boundary = count;
  for (let i = 0; i < count; i++) {
    const lo = edges[i];
    const hi = edges[i + 1];
    if (lo === undefined || hi === undefined) continue;
    if (pos < (lo + hi) / 2) {
      boundary = i;
      break;
    }
  }
  const moveIndex = boundary > fromIndex ? boundary - 1 : boundary;
  return { boundary, moveIndex };
}

/* ------------------------------------------------------------------ */
/* Selection ranges (index space)                                      */
/* ------------------------------------------------------------------ */

export interface CellRange {
  r0: number;
  c0: number;
  r1: number;
  c1: number;
}

/** Normalize an (anchor, head) index pair into a top-left/bottom-right range. */
export function normalizeRange(
  anchorRow: number,
  anchorCol: number,
  headRow: number,
  headCol: number,
): CellRange {
  return {
    r0: Math.min(anchorRow, headRow),
    c0: Math.min(anchorCol, headCol),
    r1: Math.max(anchorRow, headRow),
    c1: Math.max(anchorCol, headCol),
  };
}

/** Clamped single-step navigation (arrows/Tab) in index space. */
export function stepIndex(index: number, delta: number, count: number): number {
  if (count <= 0) return 0;
  return Math.min(count - 1, Math.max(0, index + delta));
}

/* ------------------------------------------------------------------ */
/* F-30 drop-target resolution (board-space cell rect math)            */
/* ------------------------------------------------------------------ */

export interface CellRectHit {
  row: number;
  col: number;
  /** Cell rect in the same (board, wrapper-relative) space as the edges. */
  rect: { left: number; top: number; width: number; height: number };
}

/**
 * Resolve a wrapper-relative board-space point to the cell under it.
 * Used for F-30 image-drop targeting: pointer over a cell → image into that
 * exact cell; outside every cell → null (floating element path).
 */
export function cellAtPosition(
  colEdges: readonly number[],
  rowEdges: readonly number[],
  x: number,
  y: number,
): CellRectHit | null {
  const col = indexAtPosition(colEdges, x);
  const row = indexAtPosition(rowEdges, y);
  if (col < 0 || row < 0) return null;
  const left = colEdges[col];
  const right = colEdges[col + 1];
  const top = rowEdges[row];
  const bottom = rowEdges[row + 1];
  if (left === undefined || right === undefined || top === undefined || bottom === undefined) {
    return null;
  }
  return { row, col, rect: { left, top, width: right - left, height: bottom - top } };
}
